import { ROLES } from 'dashboard/constants/permissions.js';

export const hasPermissions = (
  requiredPermissions = [],
  availablePermissions = []
) => {
  return requiredPermissions.some(permission =>
    availablePermissions.includes(permission)
  );
};

// NOOVI (custom-roles-tab-gate): para um usuário COM custom role, o papel-base
// (agent/administrator) NÃO deve conceder acesso às abas de conversa
// (unassigned/all) — valem apenas as permissões de conversa da role. Sem custom
// role (agente/admin padrão), mantém o acesso total por papel-base.
//
// Motivo: sob DISABLE_ENTERPRISE (deploy NooviChat) o AccountUser#permissions base
// inclui o papel-base ('agent') JUNTO com 'custom_role' e as permissões da role.
// Como ASSIGNEE_TYPE_TAB_PERMISSIONS lista os papéis-base (...ROLES), o 'agent'
// furava a restrição e o agente via "Todos"/"Não atribuídos" mesmo sem
// conversation_manage/conversation_unassigned_manage. Filtrar os papéis-base aqui
// faz o gate respeitar só as permissões de conversa da custom role.
export const conversationTabPermissions = (userPermissions = []) => {
  if (!userPermissions.includes('custom_role')) return userPermissions;

  return userPermissions.filter(permission => !ROLES.includes(permission));
};

// Desestruturar não basta: o default do parâmetro só cobre `undefined`, então
// `null` (usuário deslogado) estoura na própria desestruturação e
// `{ accounts: null }` estoura no `.find`. Nos dois casos quem cai é quem
// chamou — a sidebar inteira, por exemplo, não só o item ausente.
export const getCurrentAccount = (user, accountId = null) => {
  const accounts = user?.accounts;
  if (!Array.isArray(accounts)) return undefined;

  return accounts.find(account => Number(account.id) === Number(accountId));
};

export const getUserPermissions = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  return currentAccount.permissions || [];
};

export const getUserRole = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  if (currentAccount.custom_role_id) {
    return 'custom_role';
  }

  return currentAccount.role || 'agent';
};

/**
 * Filters and transforms items based on user permissions.
 *
 * @param {Object} items - An object containing items to be filtered.
 * @param {Array} userPermissions - Array of permissions the user has.
 * @param {Function} getPermissions - Function to extract required permissions from an item.
 * @param {Function} [transformItem] - Optional function to transform each item after filtering.
 * @returns {Array} Filtered and transformed items.
 */
export const filterItemsByPermission = (
  items,
  userPermissions,
  getPermissions,
  transformItem = (key, item) => ({ key, ...item })
) => {
  // Helper function to check if an item has the required permissions
  const hasRequiredPermissions = item => {
    const requiredPermissions = getPermissions(item);
    return (
      requiredPermissions.length === 0 ||
      hasPermissions(requiredPermissions, userPermissions)
    );
  };

  return Object.entries(items)
    .filter(([, item]) => hasRequiredPermissions(item)) // Keep only items with required permissions
    .map(([key, item]) => transformItem(key, item)); // Transform each remaining item
};
